import { QueryKey } from '@tanstack/react-query'
import { ACCOUNT_PERMISSION } from '../../shared/constants'
import { ObjectProperty, ObjectPropertyValue } from '../../shared/types'
import { ObjectTypes } from '../api/object'
import { PropertyFormatter } from '../objects/formatters'

export type LayoutElementComponent = {
	type: ObjectProperty['type']
	options?: ObjectProperty['options']
	name: string
	identifier: string
	isEditable?: boolean
	defaultValue?: string | number | Array<string> | boolean | null
	inputProps?: any // eslint-disable-line @typescript-eslint/no-explicit-any
	tooltip?: DetailLayout
	style?: Record<string, string>
}

export type LayoutElement =
	| { type: '__header'; text: string }
	| { type: '__text'; text: string }
	| { type: '__button'; variant: 'PRIMARY' | 'SECONDARY'; text: string }
	| LayoutElementComponent
	| {
			type: 'actionRow'
			action: string
			layout: { field: TableColumn; name: string; object: keyof ObjectTypes }
			title: string
			style?: Record<string, string>
	  }
	| {
			type: 'section'
			columns?: number
			components: Array<LayoutElement>
	  }
	| {
			type: 'condition'
			conditions: Array<{
				when: { field: string; value: ObjectPropertyValue }
				then: Array<LayoutElement>
			}>
			else?: Array<LayoutElement>
	  }

export type FormLayout = Array<LayoutElement>

export function isLayoutElementComponent(
	element: LayoutElement,
): element is LayoutElementComponent {
	return Object.hasOwn(element, 'identifier')
}

export type TableMultiaction = {
	action: string
	object: keyof ObjectTypes
	id?: string
	label: string
	formLayout?: FormLayout
	heading?: string
	invalidateQueries?: Array<QueryKey>
	direct?: boolean
}

export type TableColumn = ObjectProperty & {
	key: string
	label: string
	size?: string
	sortable?: boolean
	type?: ObjectProperty['type']
	referenceTo?: ObjectProperty['referenceTo']
	formatter?: string
}

export type TableLayout = Readonly<{
	base?: string // string 'root' will switch to `/svc/${object}/${id}`
	name: {
		singular: string
		plural: string
	}
	actions: Partial<{
		create: boolean
		createRelation: boolean
		delete: boolean
		edit: boolean
		filter: boolean
		multiselect: boolean
		view: boolean
	}>
	aggregate?: {
		type: PropertyFormatter
	}
	multiactions?: Array<TableMultiaction>
	permissions: Partial<{
		edit: (typeof ACCOUNT_PERMISSION)[keyof typeof ACCOUNT_PERMISSION]
		delete: (typeof ACCOUNT_PERMISSION)[keyof typeof ACCOUNT_PERMISSION]
	}>
	columns: Array<TableColumn>
}>

export type DetailLayout = Readonly<{
	name: {
		singular: string
		plural: string
	}
	columns: Array<TableColumn>
}>

export type RelatedTableLayout = Readonly<{
	join: Record<string, string>
	object: keyof ObjectTypes
	name: string
}>

export type Widget = Readonly<
	| {
			type: 'formInPlace'
			name?: string
			style?: Record<string, string>
	  }
	| {
			type: 'relatedFormInPlace'
			object: keyof ObjectTypes
			field: string
			name?: string
			title?: string
	  }
	| { type: 'activity'; style: Record<string, string>; action: string; fields: Array<string> }
	| {
			type: 'relatedTable'
			layout: RelatedTableLayout
	  }
	| { type: 'widgetSum' }
	| { type: 'paper'; layout: PageLayout }
>

export type PageLayout = Readonly<{
	subsections?: Array<{
		name: string
		path: string
	}>
	widgets?: Array<Widget>
}>
